import { z } from "zod";

export function isHeadphoneAvailable(productId: string, colourId: string) {
  return !(productId === "no-5909" && colourId === "pearl-black");
}
export function assertHeadphonesAvailable(items: { productId: string; colourId: string }[]) {
  if (items.some(item => !isHeadphoneAvailable(item.productId, item.colourId))) {
    throw new Error("Mark Levinson No. 5909 in Pearl Black is out of stock. Please choose Ice Pewter or Radiant Red.");
  }
}

export const headphoneItemsInput = z.array(z.object({
  productId: z.enum(["solitaire-t", "no-5909"]),
  colourId: z.string().max(30),
  quantity: z.number().int().min(1).max(5),
}).strict()).min(1).max(5);
export const headphoneQuoteInput = z.object({ items: headphoneItemsInput, code: z.string().trim().max(40).default("") }).strict();
export const headphoneCheckoutInput = headphoneQuoteInput.extend({
  id: z.string().uuid(),
  token: z.string().regex(/^[a-f0-9]{64}$/),
  customer: z.object({
    firstName: z.string().trim().min(1).max(80), lastName: z.string().trim().min(1).max(80),
    email: z.string().trim().email().max(254).transform(s => s.toLowerCase()),
    phone: z.string().trim().transform(s => s.replace(/[\s()-]/g, "")).refine(s => /^(\+65)?[89]\d{7}$/.test(s), "Enter a valid Singapore mobile number"),
    address1: z.string().trim().max(300).default(""), address2: z.string().trim().max(80).default(""),
    postalCode: z.string().trim().max(6).default(""),
  }).strict(),
  fulfilment: z.enum(["delivery", "collection"]), consent: z.literal(true),
  source: z.object({ path: z.string().max(500), referrer: z.string().max(500).default(""),
    utm_source: z.string().max(120).default(""), utm_medium: z.string().max(120).default(""),
    utm_campaign: z.string().max(120).default(""), utm_content: z.string().max(120).default(""), utm_term: z.string().max(120).default("") }).strict(),
}).superRefine((value, ctx) => {
  if (value.fulfilment === "delivery" && (!value.customer.address1 || !/^\d{6}$/.test(value.customer.postalCode))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["customer", "address1"], message: "A Singapore delivery address and six-digit postal code are required" });
  }
});
export type HeadphoneCheckout = z.infer<typeof headphoneCheckoutInput>;
// GST-inclusive SGD cents. Only server-side quotation is payment authority.
const variants: Record<string, { name: string; cents: number; discount: number }> = {
  "no-5909:pearl-black": { name: "Mark Levinson No. 5909 · Pearl Black", cents: 165000, discount: 50 },
  "no-5909:ice-pewter": { name: "Mark Levinson No. 5909 · Ice Pewter", cents: 165000, discount: 50 },
  "no-5909:radiant-red": { name: "Mark Levinson No. 5909 · Radiant Red", cents: 165000, discount: 50 },
  "solitaire-t:black": { name: "T+A Solitaire T · Black", cents: 265000, discount: 20 },
  "solitaire-t:cognac-white": { name: "T+A Solitaire T · Cognac White", cents: 275000, discount: 20 },
};
export function quoteHeadphones(raw: z.input<typeof headphoneQuoteInput>, validCode: string) {
  const input = headphoneQuoteInput.parse(raw);
  const code = input.code.toUpperCase();
  if (code && (!validCode || code !== validCode.toUpperCase())) throw new Error("Invalid discount code");
  const seen = new Set<string>();
  const lines = input.items.map(item => {
    const key = `${item.productId}:${item.colourId}`, variant = variants[key];
    if (!variant || seen.has(key)) throw new Error("Invalid or duplicate product variant");
    seen.add(key);
    const subtotal = variant.cents * item.quantity;
    const discount = code ? Math.round(subtotal * variant.discount / 100) : 0;
    return { ...item, name: variant.name, subtotal, discount, total: subtotal - discount };
  });
  return { lines, code, currency: "SGD" as const, delivery: 0,
    subtotal: lines.reduce((n, l) => n + l.subtotal, 0), discount: lines.reduce((n, l) => n + l.discount, 0),
    total: lines.reduce((n, l) => n + l.total, 0) };
}
export type HeadphoneQuote = ReturnType<typeof quoteHeadphones>;

export function validateHeadphonePayment(order: { id: string; total: number; paymentRequestId: string | null }, payload: any) {
  const payment = payload.payments?.find((p: any) => p.status === "succeeded");
  if (!order.paymentRequestId || payload.id !== order.paymentRequestId || payload.reference_number !== `HP-${order.id}` || payload.status !== "completed" || !payment?.id) throw new Error("Payment identity or status mismatch");
  for (const value of [payload, payment]) {
    if (String(value.currency).toUpperCase() !== "SGD" || !/^\d+(\.\d{1,2})?$/.test(String(value.amount)) || Math.round(Number(value.amount) * 100) !== order.total) throw new Error("Payment amount or currency mismatch");
  }
  return String(payment.id);
}
